import { useKingdom } from '../hooks/useKingdom';
import { useProvince } from '../hooks/useProvince';
import { useAuth } from '../contexts/AuthContext';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { Spinner } from '../components/Spinner';
import { DesktopKingdomViewerShell } from '../components/kingdomViewer/DesktopKingdomViewerShell';
import { MobileKingdomViewerShell } from '../components/kingdomViewer/MobileKingdomViewerShell';
import type { KingdomViewerShellProps } from '../components/kingdomViewer/types';
import { COLORS } from '../constants/theme';

/**
 * "My Kingdom" — the player's own kingdom overview + member roster.
 *
 * Route: /kingdom/roster (no id param — SF-134 split the "browse any kingdom" capability out to
 * Dossier's picker at /world/dossier/:kingdomId?). Always resolves the player's own kingdom
 * from province.metadata.location.kingdomId.
 *
 * SF-151: thin route wrapper — delegates rendering to the same DesktopKingdomViewerShell/
 * MobileKingdomViewerShell components KingdomViewer.tsx ("World Viewer") uses. Omits `browse`
 * entirely to preserve the existing own-kingdom-only, no-browsing-chrome scope (SF-134). The
 * banner image stays page-owned — it's specific to My Kingdom's identity, not the shared shells.
 */
export function Kingdom() {
  const { provinceId } = useAuth();
  const { province } = useProvince();
  const platform = useAppShellPlatform();

  const kingdomId = province?.metadata.location.kingdomId ?? null;
  const { kingdom, isLoading, error, refresh } = useKingdom(kingdomId);

  // Province not loaded yet — no fetch has been attempted, distinct from useKingdom's own
  // isLoading (which is false until a kingdomId exists to fetch).
  if (!kingdomId || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: COLORS.bg }}>
        <Spinner />
      </div>
    );
  }

  const shellProps: KingdomViewerShellProps = {
    kingdom,
    isLoading,
    error,
    viewerProvinceId: provinceId ?? null,
    pageTitle: 'My Kingdom',
    emptyMessage: 'Unable to load your kingdom.',
    refresh,
  };

  return (
    <div style={{ backgroundColor: COLORS.bg }}>
      <div className="mb-1 flex justify-center pt-0.5">
        <img
          src="/kingdom-banner.jpg"
          alt=""
          className="rounded max-w-full h-auto max-h-[120px] lg:max-h-[200px]"
          style={{ border: `1px solid ${COLORS.border}` }}
        />
      </div>
      {platform === 'mobile'
        ? <MobileKingdomViewerShell {...shellProps} />
        : <DesktopKingdomViewerShell {...shellProps} />}
    </div>
  );
}
